// The alert grid — who hears which sensor event, with which sound, when the
// premises are 🔓 UNLOCKED and when they are 🔒 LOCKED. Pure: no I/O, so the
// round trip rules → grid → rules is testable. The server side is
// Home Automation api/new/security.js (RfAlertRule).
//
// ── One CELL = one sensor × one event × one mode ───────────────────────────
//   who     'default'  no rules — exactly what happened before this existed
//           'nobody'   told to nobody (stored as one UserId-0 row, Notify 0)
//           'all'      everyone TICKED in the sensor's "Who is told" (since
//                      2026-10-10 there is no wider default audience)
//           [ids]      exactly these people
//   sound   '' = the event's / type's own sound
//
// ⚠️ "NOBODY" MUST BE STORED. With no rows the server falls back to the old
// behaviour, so an empty tick list that wrote nothing would quietly mean
// "as before", not "silent". A UserId-0 row with Notify 0 says silence.
//
// ⚠️ LIFE SAFETY (panic, fire, gas, water) has ONE column: the server ignores
// the mode for it and will never let it reach nobody — the grid says so rather
// than offering a choice the server would overrule.

import { rfType, rfEventChoices } from '../devices/rfTypes';

export const MODES = ['unlocked', 'locked'];
export const LIFE_SAFETY = ['panic', 'fire', 'gas', 'water'];
export const isLifeSafety = (type) => LIFE_SAFETY.includes(String(type || '').toLowerCase());

const CLOSE = /^(close|closed|shut|normal|restore|stop)$/i;

const key = (rfDeviceId, meaning, mode) => `${rfDeviceId}|${meaning}|${mode}`;
export const cellKey = key;

/**
 * Rows of the grid, one per sensor event.
 * -> [{ rfDeviceId, name, type, icon, room, meaning, lifeSafety }]
 */
export function gridRows(sensors) {
  const out = [];
  (sensors || []).forEach((s) => {
    const t = rfType(s.RfType);
    const events = (s.Events && s.Events.length) ? s.Events : rfEventChoices(s.RfType);
    events.forEach((meaning) => out.push({
      rfDeviceId: Number(s.RfDeviceId),
      name: String(s.Name || '').trim() || t.label,
      type: t.key,
      icon: t.icon,
      room: String(s.RoomName || '').trim(),
      meaning,
      lifeSafety: isLifeSafety(t.key),
    }));
  });
  return out;
}

/** The modes a row has cells for. */
export const rowModes = (row) => (row.lifeSafety ? ['unlocked'] : MODES);

/**
 * Server rules → { [cellKey]: { who, sound } } for the given rows.
 * A rule with Meaning '' (every event) fills each event of that sensor that
 * has no exact rule of its own.
 */
export function rulesToGrid(rules, rows) {
  const grid = {};
  (rows || []).forEach((row) => {
    rowModes(row).forEach((mode) => {
      const mine = (rules || []).filter((r) => Number(r.RfDeviceId) === row.rfDeviceId
        && String(r.Mode) === mode);
      const exact = mine.filter((r) => String(r.Meaning) === row.meaning);
      const use = exact.length ? exact : mine.filter((r) => String(r.Meaning) === '');
      if (!use.length) { grid[key(row.rfDeviceId, row.meaning, mode)] = { who: 'default', sound: '' }; return; }
      const everyone = use.find((r) => Number(r.UserId) === 0);
      const people = use.filter((r) => Number(r.UserId) > 0 && Number(r.Notify) === 1).map((r) => Number(r.UserId));
      let who;
      if (everyone && Number(everyone.Notify) === 1) who = 'all';
      else if (people.length) who = people;
      else who = 'nobody';
      const sound = String((everyone && everyone.Sound) || (use.find((r) => r.Sound) || {}).Sound || '');
      grid[key(row.rfDeviceId, row.meaning, mode)] = { who, sound };
    });
  });
  return grid;
}

/** { [cellKey]: cell } → the rows SaveRfAlertRules replaces the sensors' rules with. */
export function gridToRules(grid, rows) {
  const out = [];
  (rows || []).forEach((row) => {
    rowModes(row).forEach((mode) => {
      const c = grid[key(row.rfDeviceId, row.meaning, mode)];
      if (!c || c.who === 'default') return;
      const base = { RfDeviceId: row.rfDeviceId, Meaning: row.meaning, Mode: mode };
      if (c.who === 'nobody') out.push({ ...base, UserId: 0, Notify: 0, Sound: '' });
      else if (c.who === 'all') out.push({ ...base, UserId: 0, Notify: 1, Sound: c.sound || '' });
      else (c.who || []).forEach((u) => out.push({ ...base, UserId: Number(u), Notify: 1, Sound: c.sound || '' }));
    });
  });
  return out;
}

// ── Presets — fill the whole grid in one tap, then adjust ──────────────────
// Sounds are ones already in every APK (main_door_*, smartsecure, sound3), so a
// preset never names a sound an older phone lacks.
const isOpening = (row) => !CLOSE.test(row.meaning);
const loudTypes = ['safe', 'door', 'window', 'shutter'];

export const PRESETS = [
  {
    key: 'quiet_home',
    label: 'Quiet home',
    hint: 'Home: a chime when a door opens. Locked: everyone, loud, for any opening.',
    cell: (row, mode) => {
      if (mode === 'unlocked') {
        if (row.type === 'safe' && isOpening(row)) return { who: 'all', sound: 'smartsecure' };
        if (loudTypes.includes(row.type) && isOpening(row)) return { who: 'all', sound: 'main_door_opened' };
        return { who: 'nobody', sound: '' };
      }
      return isOpening(row) ? { who: 'all', sound: 'smartsecure' } : { who: 'nobody', sound: '' };
    },
  },
  {
    key: 'away_loud',
    label: 'Away — everyone loud',
    hint: 'Both modes: every opening and every motion, to everyone, loud.',
    cell: (row) => (isOpening(row) ? { who: 'all', sound: 'smartsecure' } : { who: 'nobody', sound: '' }),
  },
  {
    key: 'night',
    label: 'Night',
    hint: 'Home: doors chime. Locked: doors and safe loud, motion OFF (people move inside at night).',
    cell: (row, mode) => {
      if (mode === 'locked' && row.type === 'motion') return { who: 'nobody', sound: '' };
      if (mode === 'locked') return isOpening(row) ? { who: 'all', sound: 'smartsecure' } : { who: 'nobody', sound: '' };
      if (loudTypes.includes(row.type) && isOpening(row)) return { who: 'all', sound: 'main_door_opened' };
      return { who: 'nobody', sound: '' };
    },
  },
  {
    key: 'default',
    label: 'Back to default',
    hint: 'Remove every rule here — each sensor uses its own settings again.',
    cell: () => ({ who: 'default', sound: '' }),
  },
];

/**
 * Apply a preset to every cell. Life-safety rows are never switched off by a
 * preset — they stay as they are (or default), the server keeps them on anyway.
 */
export function applyPreset(presetKey, rows, grid = {}) {
  const p = PRESETS.find((x) => x.key === presetKey);
  if (!p) return grid;
  const next = { ...grid };
  (rows || []).forEach((row) => {
    rowModes(row).forEach((mode) => {
      const k = key(row.rfDeviceId, row.meaning, mode);
      if (row.lifeSafety && p.key !== 'default') {
        next[k] = next[k] && next[k].who !== 'nobody' ? next[k] : { who: 'all', sound: '' };
        return;
      }
      next[k] = p.cell(row, mode);
    });
  });
  return next;
}

/** "Same as 🔓" — copy the unlocked column into the locked one. */
export function copyColumn(grid, rows, from = 'unlocked', to = 'locked') {
  const next = { ...grid };
  (rows || []).forEach((row) => {
    if (row.lifeSafety) return;
    const src = next[key(row.rfDeviceId, row.meaning, from)];
    if (src) next[key(row.rfDeviceId, row.meaning, to)] = { ...src, who: Array.isArray(src.who) ? [...src.who] : src.who };
  });
  return next;
}

/** Short text for a cell's "who", for the select's closed state. */
export function whoLabel(who, users = []) {
  if (who === 'default') return 'Default';
  if (who === 'nobody') return 'Nobody';
  if (who === 'all') return 'Everyone';
  const ids = Array.isArray(who) ? who : [];
  if (!ids.length) return 'Nobody';
  const names = ids.map((u) => {
    const x = users.find((p) => Number(p.UserId) === Number(u));
    return x ? (String(x.FullName || '').split(' ').pop() || `User ${u}`) : `User ${u}`;
  });
  return names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ');
}
